import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AuteurEstimation,
  CreationEstimation,
  Estimation,
  Police,
  OeuvreAssurance,
  PoliceProposee,
  ReglagesAssurance,
  RaisonReevaluation,
} from '@artosera/shared';
import { api, requeteTelechargement, type AppelApi } from './client.js';
import { appelDeRequete, cleMetier, dansLaPortee, dansLaPorteeCourante } from './cles.js';
import { useMutation } from './mutation.js';
import { modeDemonstrationActif } from './demo.js';
import { ApiError, estApiError } from './errors.js';
import i18n from '../i18n.js';
import type { ReponseDocumentSigne } from './documentsGalerie.js';
import type { Pagination } from './types.js';

export type EstimationPresentee = Omit<Estimation, 'dateEffet' | 'creeLe'> & {
  dateEffet: string;
  creeLe: string;
};
export type PolicePresentee = Omit<Police, 'debut' | 'fin' | 'modifieLe'> & {
  debut: string;
  fin: string;
  modifieLe: string;
};
export type CorpsEstimation = Omit<CreationEstimation, 'dateEffet'> & { dateEffet: string };
export type CorpsPolice = Omit<PolicePresentee, '_id' | 'creePar' | 'modifieLe' | 'revision'>;
export type OeuvreAssurancePresentee = OeuvreAssurance & {
  titre: string;
  numeroInventaire: string;
  artisteNom: string;
  devise: Estimation['devise'];
};
export interface TotalLieu {
  lieuId: string | null;
  devise: Estimation['devise'];
  totalCentimes: number;
}
export interface BilanPolice {
  police: PolicePresentee;
  oeuvresCouvertes: { oeuvre: OeuvreAssurancePresentee; raison: 'lieu' | 'rattachement' }[];
  totauxParLieu: TotalLieu[];
  totalCentimes: number;
  depassementGlobal: boolean;
  depassementsOeuvres: { oeuvreId: string; montantCentimes: number }[];
  devisesDifferentes: {
    oeuvreId: string;
    montantCentimes: number;
    devise: Estimation['devise'];
    raison: 'devise_differente';
  }[];
}
export type RaisonPresentee =
  | Extract<RaisonReevaluation, { type: 'periodicite' }>
  | {
      type: 'vente_comparable';
      vente: { oeuvreId: string; prixCentimes: number; devise: Estimation['devise']; date: string };
    };
export interface TableauAssurance {
  aReevaluer: { oeuvre: OeuvreAssurancePresentee; raisons: RaisonPresentee[] }[];
  sansValeur: OeuvreAssurancePresentee[];
  totauxParLieu: TotalLieu[];
  totauxParPolice: BilanPolice[];
  echeances: { police: PolicePresentee; jalon: 'j60' | 'j30' | 'echue' }[];
  calculeLe: string;
}
export interface AssuranceOeuvre {
  valeurCourante: EstimationPresentee | null;
  historique: EstimationPresentee[];
  suggestion?: { montantCentimes: number; devise: Estimation['devise']; source: 'prix_public' };
  couverture: { police: PolicePresentee; raison: 'lieu' | 'rattachement' }[];
  exemplaires: { exemplaireId: string; valeurCourante: EstimationPresentee | null }[];
}
export interface ValeursAssurance {
  lignes: {
    oeuvreId: string;
    valeurCentimes?: number;
    devise: Estimation['devise'];
    dateEffet?: string;
  }[];
  policeProposee?: PoliceProposee;
}
export type CorpsLot = { dateEffet: string; auteur: AuteurEstimation; justification?: string } & (
  | { mode: 'pourcentage'; pourcentage: number; oeuvreIds: string[] }
  | { mode: 'tableau'; lignes: { oeuvreId: string; montantCentimes: number }[] }
);
export interface SelectionAnnexe {
  policeId?: string;
  oeuvreIds?: string[];
  langue: 'fr' | 'en';
}
const chemin = (g: string, suite: string) => `/g/${encodeURIComponent(g)}${suite}`;
function options(galerie: string, appel?: AppelApi) {
  if (modeDemonstrationActif())
    throw new ApiError(403, 'module.indisponible', i18n.t('regie:assurance.demoIndisponible'));
  return { galerie, ...appel };
}
export function messageErreurAssurance(erreur: unknown): string {
  if (!estApiError(erreur)) return i18n.t('commun:erreurReseau');
  const raison = (erreur.details as { raison?: string } | null)?.raison;
  if (raison && i18n.exists(`erreurs:assurance.${raison}`))
    return i18n.t(`erreurs:assurance.${raison}`);
  return erreur.message;
}
export const assuranceApi = {
  tableau: (g: string, a?: AppelApi) =>
    api.get<TableauAssurance>(chemin(g, '/assurance/tableau'), options(g, a)),
  oeuvre: (g: string, id: string, a?: AppelApi) =>
    api.get<AssuranceOeuvre>(
      chemin(g, `/oeuvres/${encodeURIComponent(id)}/estimations`),
      options(g, a),
    ),
  estimations: (
    g: string,
    filtres: {
      oeuvreId?: string;
      type?: Estimation['type'];
      depuis?: string;
      jusqua?: string;
      page?: number;
      parPage?: number;
    } = {},
    a?: AppelApi,
  ) => {
    const query = new URLSearchParams();
    for (const [cle, valeur] of Object.entries(filtres))
      if (valeur !== undefined) query.set(cle, String(valeur));
    return api.get<{ estimations: EstimationPresentee[]; pagination: Pagination }>(
      chemin(g, `/estimations?${query}`),
      options(g, a),
    );
  },
  estimation: (g: string, id: string, a?: AppelApi) =>
    api.get<{ estimation: EstimationPresentee & { annuleePar?: string } }>(
      chemin(g, `/estimations/${encodeURIComponent(id)}`),
      options(g, a),
    ),
  creerEstimation: (g: string, id: string, corps: CorpsEstimation, a?: AppelApi) =>
    api.post<{ estimation: EstimationPresentee }>(
      chemin(g, `/oeuvres/${encodeURIComponent(id)}/estimations`),
      corps,
      options(g, a),
    ),
  annulerEstimation: (
    g: string,
    id: string,
    corps: {
      motif: string;
      montantCentimes?: number;
      dateEffet?: string;
      auteur?: AuteurEstimation;
      justification?: string;
    },
    a?: AppelApi,
  ) =>
    api.post<{ estimation: EstimationPresentee }>(
      chemin(g, `/estimations/${encodeURIComponent(id)}/annulation`),
      corps,
      options(g, a),
    ),
  polices: (g: string, a?: AppelApi) =>
    api.get<{ polices: BilanPolice[] }>(chemin(g, '/polices'), options(g, a)),
  police: (g: string, id: string, a?: AppelApi) =>
    api.get<BilanPolice>(chemin(g, `/polices/${encodeURIComponent(id)}`), options(g, a)),
  creerPolice: (g: string, corps: CorpsPolice, a?: AppelApi) =>
    api.post<{ police: PolicePresentee }>(chemin(g, '/polices'), corps, options(g, a)),
  modifierPolice: (
    g: string,
    id: string,
    corps: Partial<CorpsPolice> & { revision: number },
    a?: AppelApi,
  ) =>
    api.patch<{ police: PolicePresentee }>(
      chemin(g, `/polices/${encodeURIComponent(id)}`),
      corps,
      options(g, a),
    ),
  archiverPolice: (g: string, id: string, revision: number, a?: AppelApi) =>
    api.post<{ police: PolicePresentee }>(
      chemin(g, `/polices/${encodeURIComponent(id)}/archivage`),
      { revision },
      options(g, a),
    ),
  valeurs: (g: string, ids: string[], a?: AppelApi) =>
    api.get<ValeursAssurance>(
      chemin(g, `/assurance/valeurs?${new URLSearchParams({ oeuvreIds: ids.join(',') })}`),
      options(g, a),
    ),
  reevaluer: (g: string, corps: CorpsLot, a?: AppelApi) =>
    api.post<{ creees: number }>(chemin(g, '/assurance/reevaluation-lot'), corps, options(g, a)),
  reglages: (g: string, a?: AppelApi) =>
    api.get<{ reglages: ReglagesAssurance }>(chemin(g, '/assurance/reglages'), options(g, a)),
  modifierReglages: (g: string, corps: Omit<ReglagesAssurance, 'cle'>, a?: AppelApi) =>
    api.put<{ reglages: ReglagesAssurance }>(
      chemin(g, '/assurance/reglages'),
      corps,
      options(g, a),
    ),
  annexePdf: (g: string, selection: SelectionAnnexe, a?: AppelApi) =>
    api.post<ReponseDocumentSigne>(chemin(g, '/assurance/annexe.pdf'), selection, options(g, a)),
  annexeTableur: (g: string, format: 'csv' | 'xlsx', selection: SelectionAnnexe, a?: AppelApi) => {
    const query = new URLSearchParams({ langue: selection.langue });
    if (selection.policeId) query.set('policeId', selection.policeId);
    if (selection.oeuvreIds) query.set('oeuvreIds', selection.oeuvreIds.join(','));
    return requeteTelechargement(chemin(g, `/assurance/annexe.${format}?${query}`), options(g, a));
  },
};
export function useTableauAssurance(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'assurance', 'tableau'),
    queryFn: (c) => assuranceApi.tableau(g, appelDeRequete(c)),
  });
}
export function useAssuranceOeuvre(g: string, id: string) {
  return useQuery({
    queryKey: cleMetier(g, 'assurance', 'oeuvre', id),
    queryFn: (c) => assuranceApi.oeuvre(g, id, appelDeRequete(c)),
  });
}
export function usePolices(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'assurance', 'polices'),
    queryFn: (c) => assuranceApi.polices(g, appelDeRequete(c)),
  });
}
export function useReglagesAssurance(g: string) {
  return useQuery({
    queryKey: cleMetier(g, 'assurance', 'reglages'),
    queryFn: (c) => assuranceApi.reglages(g, appelDeRequete(c)),
    select: (r) => r.reglages,
  });
}
export function useValeursAssurance(g: string, ids: string[], actif: boolean) {
  return useQuery({
    queryKey: cleMetier(g, 'assurance', 'valeurs', ids),
    queryFn: (c) => assuranceApi.valeurs(g, ids, appelDeRequete(c)),
    enabled: actif && ids.length > 0,
  });
}
function useGeste<T, R>(g: string, executer: (v: T, a: AppelApi) => Promise<R>) {
  const client = useQueryClient();
  return useMutation(
    dansLaPortee({
      mutationFn: executer,
      onSuccess: () => {
        for (const collection of ['assurance', 'mouvements'])
          void client.invalidateQueries({ queryKey: cleMetier(g, collection) });
      },
    }),
  );
}
export function useCreerEstimation(g: string, id: string) {
  return useGeste(g, (c: CorpsEstimation, a) => assuranceApi.creerEstimation(g, id, c, a));
}
export function useAnnulerEstimation(g: string) {
  return useGeste(g, (v: { id: string; motif: string; montantCentimes?: number }, a) =>
    assuranceApi.annulerEstimation(
      g,
      v.id,
      {
        motif: v.motif,
        ...(v.montantCentimes === undefined ? {} : { montantCentimes: v.montantCentimes }),
      },
      a,
    ),
  );
}
export function useReevaluer(g: string) {
  return useGeste(g, (c: CorpsLot, a) => assuranceApi.reevaluer(g, c, a));
}
export function useEnregistrerPolice(g: string) {
  return useGeste(g, (v: { id?: string; revision?: number; corps: CorpsPolice }, a) =>
    v.id
      ? assuranceApi.modifierPolice(g, v.id, { ...v.corps, revision: v.revision! }, a)
      : assuranceApi.creerPolice(g, v.corps, a),
  );
}
export function useArchiverPolice(g: string) {
  return useGeste(g, (p: PolicePresentee, a) =>
    assuranceApi.archiverPolice(g, p._id, p.revision, a),
  );
}
export function useModifierReglagesAssurance(g: string) {
  return useGeste(g, (c: Omit<ReglagesAssurance, 'cle'>, a) =>
    assuranceApi.modifierReglages(g, c, a),
  );
}
export function useAnnexeAssurance(g: string) {
  return (format: 'pdf' | 'csv' | 'xlsx', selection: SelectionAnnexe) =>
    dansLaPorteeCourante(async (a) =>
      format === 'pdf'
        ? assuranceApi.annexePdf(g, selection, a)
        : assuranceApi.annexeTableur(g, format, selection, a),
    );
}
